import { createBrowserRouter, redirect, type LoaderFunctionArgs } from "react-router";
import { isApiError } from "./api/client";
import { authStateQuery, meQuery } from "./api/queries";
import { keys } from "./api/keys";
import { queryClient, setUnauthorizedHandler } from "./api/queryClient";
import type { AuthState } from "./api/types";
import { AppShell } from "./app/AppShell";
import { NoOrg, NotFound, PageError, RootError } from "./pages/errors";
import { AuthLayout } from "./pages/auth/AuthLayout";
import type { ComponentType } from "react";

/*
 * The shell (sidebar, palette, New site and Add server) loads up front;
 * each page is its own chunk, fetched the first time someone opens it.
 */
function page<K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) {
  return async () => ({ Component: (await load())[name] });
}

/** Shown for the instant the first loader runs: just the canvas, no flash. */
function Boot() {
  return <div className="min-h-dvh bg-canvas" aria-busy="true" />;
}

const auth = () => queryClient.ensureQueryData(authStateQuery());

/** /api/me with a cached auth state that may be stale: a 401 means sign in again. */
async function me() {
  try {
    return await queryClient.ensureQueryData(meQuery());
  } catch (err) {
    if (isApiError(err) && err.status === 401) {
      queryClient.setQueryData<AuthState>(keys.authState, { setupRequired: false, signedIn: false, mfaPending: false });
      throw redirect("/login");
    }
    throw err;
  }
}

function lastOrgSlug(): string | null {
  try {
    return localStorage.getItem("hx-last-org");
  } catch {
    return null;
  }
}

/** Where a signed-in person lands: their last org, else their first. */
async function homePath(): Promise<string> {
  const m = await me();
  const last = lastOrgSlug();
  const org = m.orgs.find((o) => o.slug === last) ?? m.orgs[0];
  return org ? `/o/${org.slug}` : "/no-organization";
}

async function rootLoader() {
  const s = await auth();
  if (s.setupRequired) throw redirect("/setup");
  if (s.mfaPending) throw redirect("/login/2fa");
  if (!s.signedIn) throw redirect("/login");
  throw redirect(await homePath());
}

/**
 * /settings/profile, /settings/sign-in and /settings/api-tokens, from an email: that page of your account in your last
 * org, after signing in if need be.
 */
function accountLoader(page: "profile" | "sign-in" | "api-tokens") {
  return async () => {
    const s = await auth();
    if (s.setupRequired) throw redirect("/setup");
    if (s.mfaPending) throw redirect(`/login/2fa?next=${encodeURIComponent(`/settings/${page}`)}`);
    if (!s.signedIn) throw redirect(`/login?next=${encodeURIComponent(`/settings/${page}`)}`);
    const home = await homePath();
    throw redirect(home.startsWith("/o/") ? `${home}/settings/${page}` : home);
  };
}

/** An address that moved: the same page at its new one, with its query (Stripe's return to Billing carries one). */
const moved = (to: (orgSlug: string) => string) => ({ params, request }: LoaderFunctionArgs) => redirect(`${to(params.orgSlug!)}${new URL(request.url).search}`);

/** Login, setup and 2FA pages are only for people who aren't signed in yet. */
function guestLoader(page: "login" | "mfa" | "setup") {
  return async () => {
    const s = await auth();
    if (s.signedIn) throw redirect(await homePath());
    if (page === "setup" && !s.setupRequired) throw redirect("/login");
    if (page !== "setup" && s.setupRequired) throw redirect("/setup");
    if (page === "mfa" && !s.mfaPending) throw redirect("/login");
    return null;
  };
}

async function orgLoader({ params, request }: LoaderFunctionArgs) {
  const s = await auth();
  if (s.setupRequired) throw redirect("/setup");
  if (s.mfaPending) throw redirect("/login/2fa");
  if (!s.signedIn) {
    const url = new URL(request.url);
    throw redirect(`/login?next=${encodeURIComponent(url.pathname + url.search)}`);
  }
  const m = await me();
  if (!m.orgs.some((o) => o.slug === params.orgSlug)) {
    const first = m.orgs[0];
    throw redirect(first ? `/o/${first.slug}` : "/no-organization");
  }
  return null;
}

export const router = createBrowserRouter([
  { path: "/", loader: rootLoader, errorElement: <RootError />, element: null, hydrateFallbackElement: <Boot /> },
  // Security emails link to your account's pages without an org (you may be in several): they open in your last one.
  { path: "/settings/profile", loader: accountLoader("profile"), errorElement: <RootError />, element: null, hydrateFallbackElement: <Boot /> },
  { path: "/settings/sign-in", loader: accountLoader("sign-in"), errorElement: <RootError />, element: null, hydrateFallbackElement: <Boot /> },
  { path: "/settings/api-tokens", loader: accountLoader("api-tokens"), errorElement: <RootError />, element: null, hydrateFallbackElement: <Boot /> },
  {
    element: <AuthLayout />,
    errorElement: <RootError />,
    hydrateFallbackElement: <Boot />,
    children: [
      { path: "/login", loader: guestLoader("login"), lazy: page(() => import("./pages/auth/Login"), "Login") },
      { path: "/login/2fa", loader: guestLoader("mfa"), lazy: page(() => import("./pages/auth/Mfa"), "Mfa") },
      { path: "/setup", loader: guestLoader("setup"), lazy: page(() => import("./pages/auth/Setup"), "Setup") },
      { path: "/forgot", loader: guestLoader("login"), lazy: page(() => import("./pages/auth/Forgot"), "Forgot") },
      // Older "password changed" emails link here.
      { path: "/forgot-password", loader: () => redirect("/forgot") },
      // A reset link works whether or not someone is signed in on this browser.
      // The API's emails link to /reset-password?token=… and /invite?token=…; the path forms work too.
      { path: "/reset-password", lazy: page(() => import("./pages/auth/Reset"), "Reset") },
      { path: "/reset/:token", lazy: page(() => import("./pages/auth/Reset"), "Reset") },
      { path: "/invite", lazy: page(() => import("./pages/auth/AcceptInvite"), "AcceptInvite") },
      { path: "/invite/:token", lazy: page(() => import("./pages/auth/AcceptInvite"), "AcceptInvite") },
      { path: "/no-organization", element: <NoOrg /> },
    ],
  },
  {
    path: "/o/:orgSlug",
    loader: orgLoader,
    element: <AppShell />,
    errorElement: <RootError />,
    hydrateFallbackElement: <Boot />,
    children: [
      {
        errorElement: <PageError />,
        children: [
          { index: true, lazy: page(() => import("./pages/Overview"), "Overview") },
          { path: "sites", lazy: page(() => import("./pages/sites/Sites"), "Sites") },
          // Recently deleted sites are Sites' Deleted set now; before :siteId.
          { path: "sites/deleted", loader: ({ params }) => redirect(`/o/${params.orgSlug}/sites?show=deleted`) },
          { path: "sites/:siteId", lazy: page(() => import("./pages/sites/SiteDetail"), "SiteDetail") },
          // Each section of a site has its own address: /sites/:id/backups.
          { path: "sites/:siteId/:tab", lazy: page(() => import("./pages/sites/SiteDetail"), "SiteDetail") },
          // Environments first: /sites/:id/env/live/backups. Old tab addresses redirect from SiteDetail.
          { path: "sites/:siteId/env/:env", lazy: page(() => import("./pages/sites/SiteDetail"), "SiteDetail") },
          { path: "sites/:siteId/env/:env/:section", lazy: page(() => import("./pages/sites/SiteDetail"), "SiteDetail") },
          // Security's parts: /sites/:id/env/live/security/access.
          { path: "sites/:siteId/env/:env/:section/:part", lazy: page(() => import("./pages/sites/SiteDetail"), "SiteDetail") },
          // Extra environments: the list of every environment (/environments/all) and making one (/environments/new).
          { path: "sites/:siteId/environments/:envView", lazy: page(() => import("./pages/sites/SiteDetail"), "SiteDetail") },
          { path: "sites/:siteId/settings/:settingsSection", lazy: page(() => import("./pages/sites/SiteDetail"), "SiteDetail") },
          // Website feedback: one comment by its number (/feedback/12); the inbox is the Feedback tab.
          { path: "sites/:siteId/feedback/:feedbackN", lazy: page(() => import("./pages/sites/SiteDetail"), "SiteDetail") },
          // Activity by kind (/activity/deploys?env=live) and Email by domain and part (/email/example.com/dns).
          { path: "sites/:siteId/activity/:activityFilter", lazy: page(() => import("./pages/sites/SiteDetail"), "SiteDetail") },
          { path: "sites/:siteId/email/:mailDomain", lazy: page(() => import("./pages/sites/SiteDetail"), "SiteDetail") },
          { path: "sites/:siteId/email/:mailDomain/:mailSection", lazy: page(() => import("./pages/sites/SiteDetail"), "SiteDetail") },
          { path: "updates", lazy: page(() => import("./pages/updates/Updates"), "Updates") },
          { path: "mail", lazy: page(() => import("./pages/mail/Mail"), "Mail") },
          { path: "mail/mailboxes/:mailboxId", lazy: page(() => import("./pages/mail/MailboxPage"), "MailboxPage") },
          { path: "mail/mailboxes/:mailboxId/:section", lazy: page(() => import("./pages/mail/MailboxPage"), "MailboxPage") },
          { path: "mail/:domainId", lazy: page(() => import("./pages/mail/MailDomain"), "MailDomain") },
          { path: "mail/:domainId/:section", lazy: page(() => import("./pages/mail/MailDomain"), "MailDomain") },
          { path: "domains", lazy: page(() => import("./pages/domains/Domains"), "Domains") },
          { path: "domains/prices", lazy: page(() => import("./pages/domains/Prices"), "Prices") },
          { path: "domains/:domainId", lazy: page(() => import("./pages/domains/DomainDetail"), "DomainDetail") },
          // Billing is a Settings page now; emails, Stripe and older notifications link here.
          { path: "billing", loader: moved((o) => `/o/${o}/settings/billing`) },
          { path: "clients", lazy: page(() => import("./pages/clients/Clients"), "Clients") },
          { path: "accounts", lazy: page(() => import("./pages/clients/Clients"), "Clients") }, // the platform org's Clients (docs/plans.md)
          { path: "transfers/:transferId", lazy: page(() => import("./pages/transfers/TransferRequest"), "TransferRequest") },
          // Servers: for people who see them (docs/plans.md; pages/servers/Gate.tsx).
          { path: "servers", lazy: page(() => import("./pages/servers/Gate"), "ServersPage") },
          { path: "servers/:serverId", lazy: page(() => import("./pages/servers/Gate"), "ServerDetailPage") },
          // A server's tabs and their parts: /servers/:id/roles/mail, /servers/:id/activity/backups. Old addresses redirect from ServerDetail.
          { path: "servers/:serverId/:tab", lazy: page(() => import("./pages/servers/Gate"), "ServerDetailPage") },
          { path: "servers/:serverId/:tab/:part", lazy: page(() => import("./pages/servers/Gate"), "ServerDetailPage") },
          { path: "activity", lazy: page(() => import("./pages/Activity"), "Activity") },
          {
            path: "settings",
            lazy: page(() => import("./pages/settings/SettingsLayout"), "SettingsLayout"),
            children: [
              { index: true, loader: ({ params }) => redirect(`/o/${params.orgSlug}/settings/profile`) },
              // Your account (app/settingsNav.ts has the rail's groups and who sees each page).
              { path: "profile", lazy: page(() => import("./pages/settings/Profile"), "Profile") },
              { path: "sign-in", lazy: page(() => import("./pages/settings/Profile"), "SignIn") },
              { path: "ssh-keys", lazy: page(() => import("./pages/settings/SshKeys"), "SshKeys") },
              { path: "api-tokens", lazy: page(() => import("./pages/settings/ApiTokens"), "ApiTokens") },
              { path: "notifications", lazy: page(() => import("./pages/settings/Notifications"), "Notifications") },
              // The organization: General, Team (a person and a group inside it), Billing, Site tags, Alerts.
              { path: "organization", lazy: page(() => import("./pages/settings/Organization"), "Organization") },
              { path: "team", lazy: page(() => import("./pages/settings/Team"), "Team") },
              { path: "people", loader: moved((o) => `/o/${o}/settings/team`) },
              { path: "people/:userId", lazy: page(() => import("./pages/settings/Person"), "Person") },
              { path: "groups", loader: moved((o) => `/o/${o}/settings/team`) },
              { path: "groups/:groupId", lazy: page(() => import("./pages/settings/Group"), "Group") },
              { path: "billing", lazy: page(() => import("./pages/billing/Billing"), "Billing") },
              { path: "tags", lazy: page(() => import("./pages/settings/SiteTags"), "SiteTags") },
              { path: "alerts", lazy: page(() => import("./pages/settings/Alerts"), "Alerts") },
              // Its sites: security profiles (one inside it) and saved address lists.
              { path: "security", lazy: page(() => import("./pages/settings/OrgSecurity"), "OrgSecurity") },
              { path: "security/profiles/:profileId", lazy: page(() => import("./pages/settings/SecurityProfile"), "SecurityProfilePage") },
              // Its brand: the look, email and support, and its own addresses.
              { path: "branding", lazy: page(() => import("./pages/settings/Branding"), "Branding") },
              { path: "branding/email", lazy: page(() => import("./pages/settings/Branding"), "BrandingEmail") },
              { path: "branding/addresses", lazy: page(() => import("./pages/settings/Branding"), "BrandingAddresses") },
              // Hosterr's own, for its admins.
              { path: "domain-prices", lazy: page(() => import("./pages/settings/DomainPrices"), "DomainPrices") },
              { path: "plans", lazy: page(() => import("./pages/settings/Plans"), "Plans") },
              { path: "backup-policies", lazy: page(() => import("./pages/settings/BackupPolicies"), "BackupPolicies") },
              { path: "agent-updates", lazy: page(() => import("./pages/settings/AgentUpdates"), "AgentUpdates") },
              { path: "legal", lazy: page(() => import("./pages/settings/Legal"), "Legal") },
            ],
          },
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
  { path: "*", element: <NotFound standalone /> },
]);

// An expired session anywhere sends the person back to sign in, then here.
setUnauthorizedHandler(() => {
  const loc = window.location;
  if (loc.pathname.startsWith("/login") || loc.pathname.startsWith("/setup")) return;
  queryClient.setQueryData<AuthState>(keys.authState, { setupRequired: false, signedIn: false, mfaPending: false });
  queryClient.removeQueries({ queryKey: keys.me });
  void router.navigate(`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`, { replace: true });
});
